import { getSupabaseClient } from '../supabase';
import type { Lead, LeadStatus, LeadWithDraft } from '@traction/shared';

export async function getLeads(
  userId: string,
  options?: {
    status?: LeadStatus;
    limit?: number;
    offset?: number;
  }
): Promise<Lead[]> {
  let query = getSupabaseClient()
    .from('leads')
    .select('*')
    .eq('user_id', userId)
    .order('delivered_at', { ascending: false });

  if (options?.status) {
    query = query.eq('status', options.status);
  }

  if (options?.limit) {
    query = query.limit(options.limit);
  }

  if (options?.offset) {
    query = query.range(options.offset, options.offset + (options.limit ?? 10) - 1);
  }

  const { data, error } = await query;

  if (error) throw error;
  return (data ?? []) as Lead[];
}

export async function getLeadsWithDrafts(
  userId: string,
  status?: LeadStatus
): Promise<LeadWithDraft[]> {
  let query = getSupabaseClient()
    .from('leads')
    .select(`
      *,
      email_draft:email_drafts(*)
    `)
    .eq('user_id', userId)
    .order('delivered_at', { ascending: false });

  if (status) {
    query = query.eq('status', status);
  }

  const { data, error } = await query;

  if (error) throw error;

  return ((data ?? []) as any[]).map((lead) => ({
    ...lead,
    email_draft: Array.isArray(lead.email_draft)
      ? lead.email_draft.find((d: any) => d.is_current) ?? null
      : lead.email_draft,
  }));
}

/**
 * Get warm leads - leads with high scores and/or strong signals
 * Warm leads have: funding, leadership_change, hiring signals or high score
 */
export async function getWarmLeads(
  userId: string,
  options?: { limit?: number }
): Promise<LeadWithDraft[]> {
  const { data, error } = await getSupabaseClient()
    .from('leads')
    .select(`
      *,
      email_draft:email_drafts(*)
    `)
    .eq('user_id', userId)
    .or('score.eq.high,signal_type.in.(funding,leadership_change,hiring)')
    .order('delivered_at', { ascending: false })
    .limit(options?.limit ?? 50);

  if (error) throw error;

  return ((data ?? []) as any[]).map((lead) => ({
    ...lead,
    email_draft: Array.isArray(lead.email_draft)
      ? lead.email_draft.find((d: any) => d.is_current) ?? null
      : lead.email_draft,
  }));
}

/**
 * Calculate lead warmth score based on signals and data
 */
export function calculateLeadWarmth(lead: {
  score?: string;
  signal_type?: string | null;
  signal_details?: string | null;
  company_funding_stage?: string | null;
}): 'hot' | 'warm' | 'cold' {
  // Hot leads: Recent funding or leadership change with high score
  if (lead.score === 'high') {
    if (lead.signal_type === 'funding' || lead.signal_type === 'leadership_change') {
      return 'hot';
    }
    return 'warm';
  }

  // Warm leads: Any strong signal or recent Series A+
  const warmSignals = ['funding', 'hiring', 'expansion', 'leadership_change'];
  if (lead.signal_type && warmSignals.includes(lead.signal_type)) {
    return 'warm';
  }

  // Check funding stage for warmth
  const warmFundingStages = ['series_a', 'series_b', 'series_c', 'seed'];
  if (lead.company_funding_stage && warmFundingStages.includes(lead.company_funding_stage.toLowerCase())) {
    return 'warm';
  }

  return 'cold';
}

export async function getTodaysLeads(userId: string): Promise<LeadWithDraft[]> {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const { data, error } = await getSupabaseClient()
    .from('leads')
    .select(`
      *,
      email_draft:email_drafts(*)
    `)
    .eq('user_id', userId)
    .gte('delivered_at', today.toISOString())
    .order('delivered_at', { ascending: false });

  if (error) throw error;

  return ((data ?? []) as any[]).map((lead) => ({
    ...lead,
    email_draft: Array.isArray(lead.email_draft)
      ? lead.email_draft.find((d: any) => d.is_current) ?? null
      : lead.email_draft,
  }));
}

export async function getLead(leadId: string): Promise<Lead | null> {
  const { data, error } = await getSupabaseClient()
    .from('leads')
    .select('*')
    .eq('id', leadId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    throw error;
  }

  return data as Lead;
}

export async function toggleLeadStarred(
  leadId: string,
  isStarred: boolean
): Promise<void> {
  const { error } = await getSupabaseClient()
    .from('leads')
    .update({ is_starred: isStarred, updated_at: new Date().toISOString() })
    .eq('id', leadId);

  if (error) throw error;
}

export async function updateLeadContactInfo(
  leadId: string,
  field: 'email' | 'phone',
  value: string | null
): Promise<void> {
  const { error } = await getSupabaseClient()
    .from('leads')
    .update({ [field]: value, updated_at: new Date().toISOString() })
    .eq('id', leadId);

  if (error) throw error;
}

/** User override for Locals tab / grouping (stored in enrichment_data). */
export type LeadLocalOverride = 'auto' | 'local' | 'not_local';

const TRACTIONBEE_LOCAL_KEY = 'tractionbee_local_override';

export async function updateLeadLocalOverride(
  leadId: string,
  currentEnrichment: Record<string, unknown> | null,
  value: LeadLocalOverride
): Promise<void> {
  const next: Record<string, unknown> = { ...(currentEnrichment || {}) };
  if (value === 'auto') {
    delete next[TRACTIONBEE_LOCAL_KEY];
  } else {
    next[TRACTIONBEE_LOCAL_KEY] = value;
  }
  const { error } = await getSupabaseClient()
    .from('leads')
    .update({ enrichment_data: next, updated_at: new Date().toISOString() })
    .eq('id', leadId);

  if (error) throw error;
}

export async function updateLeadStatus(
  leadId: string,
  status: LeadStatus
): Promise<void> {
  const updates: Record<string, any> = { status };

  if (status === 'contacted') {
    updates.contacted_at = new Date().toISOString();
  } else if (status === 'replied') {
    updates.replied_at = new Date().toISOString();
  }

  const { error } = await getSupabaseClient()
    .from('leads')
    .update(updates)
    .eq('id', leadId);

  if (error) throw error;
}

export interface DoNotContactEntry {
  id: string;
  email: string;
  reason: string | null;
  created_at: string;
}

export async function getDoNotContactList(userId: string): Promise<DoNotContactEntry[]> {
  const { data, error } = await getSupabaseClient()
    .from('do_not_contact')
    .select('id, email, reason, created_at')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return (data ?? []) as DoNotContactEntry[];
}

export async function addToDoNotContact(
  userId: string,
  email: string,
  reason?: string
): Promise<void> {
  const { error } = await getSupabaseClient()
    .from('do_not_contact')
    .insert({
      user_id: userId,
      email: email.toLowerCase().trim(),
      reason,
    });

  if (error) {
    // Ignore duplicate errors
    if (error.code === '23505') return;
    throw error;
  }
}

export async function removeFromDoNotContact(
  userId: string,
  entryId: string
): Promise<void> {
  const { error } = await getSupabaseClient()
    .from('do_not_contact')
    .delete()
    .eq('id', entryId)
    .eq('user_id', userId);

  if (error) throw error;
}

export async function bulkAddToDoNotContact(
  userId: string,
  emails: string[]
): Promise<{ added: number; skipped: number }> {
  const results = { added: 0, skipped: 0 };

  for (const email of emails) {
    try {
      await addToDoNotContact(userId, email);
      results.added++;
    } catch {
      results.skipped++;
    }
  }

  return results;
}

export interface CSVLeadInput {
  first_name: string;
  last_name: string;
  email: string;
  title?: string;
  company_name?: string;
  company_industry?: string;
  company_size?: string;
  company_website?: string;
  linkedin_url?: string;
  location?: string;
}

export async function createLeadsFromCSV(
  userId: string,
  leads: CSVLeadInput[]
): Promise<{ created: number; skipped: number; errors: string[] }> {
  const results = {
    created: 0,
    skipped: 0,
    errors: [] as string[],
  };

  // Get existing emails for deduplication
  const { data: existingLeads } = await getSupabaseClient()
    .from('leads')
    .select('email')
    .eq('user_id', userId);

  const existingEmails = new Set(
    (existingLeads ?? []).map((l: { email: string }) => l.email.toLowerCase())
  );

  // Get do-not-contact emails
  const { data: dncList } = await getSupabaseClient()
    .from('do_not_contact')
    .select('email')
    .eq('user_id', userId);

  const dncEmails = new Set(
    (dncList ?? []).map((d: { email: string }) => d.email.toLowerCase())
  );

  const leadsToInsert = [];

  // Email validation regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  for (const lead of leads) {
    const email = lead.email?.trim().toLowerCase();

    if (!email) {
      results.errors.push(`Missing email for ${lead.first_name} ${lead.last_name}`);
      continue;
    }

    if (!emailRegex.test(email)) {
      results.errors.push(`Invalid email format: ${email}`);
      continue;
    }

    if (existingEmails.has(email)) {
      results.skipped++;
      continue;
    }

    if (dncEmails.has(email)) {
      results.skipped++;
      continue;
    }

    leadsToInsert.push({
      user_id: userId,
      first_name: lead.first_name?.trim() || 'Unknown',
      last_name: lead.last_name?.trim() || '',
      email,
      title: lead.title?.trim() || '',
      company_name: lead.company_name?.trim() || '',
      company_industry: lead.company_industry?.trim() || '',
      company_size: lead.company_size?.trim() || '',
      company_website: lead.company_website?.trim() || null,
      linkedin_url: lead.linkedin_url?.trim() || null,
      location: lead.location?.trim() || null,
      score: 'medium', // Default score for CSV imports
      status: 'pending',
      source: 'csv',
      delivered_at: new Date().toISOString(),
    });

    existingEmails.add(email); // Prevent duplicates within batch
  }

  if (leadsToInsert.length > 0) {
    const { error } = await getSupabaseClient()
      .from('leads')
      .insert(leadsToInsert);

    if (error) {
      results.errors.push(`Database error: ${error.message}`);
    } else {
      results.created = leadsToInsert.length;
    }
  }

  return results;
}

export async function recordCSVUpload(
  userId: string,
  fileName: string,
  rowCount: number,
  successCount: number,
  errorCount: number
): Promise<void> {
  const { error } = await getSupabaseClient()
    .from('csv_uploads')
    .insert({
      user_id: userId,
      file_name: fileName,
      row_count: rowCount,
      success_count: successCount,
      error_count: errorCount,
      status: errorCount > 0 ? 'partial' : 'completed',
    });

  if (error) throw error;
}

// Email Draft functions
export interface EmailDraftInput {
  subject: string;
  body: string;
  tone: 'warm' | 'cold';
}

export async function getEmailDraft(
  leadId: string
): Promise<{ id: string; subject: string; body: string; tone: 'warm' | 'cold'; version: number } | null> {
  const { data, error } = await getSupabaseClient()
    .from('email_drafts')
    .select('id, subject, body, tone, version')
    .eq('lead_id', leadId)
    .eq('is_current', true)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    throw error;
  }

  return data;
}

export async function saveEmailDraft(
  userId: string,
  leadId: string,
  draft: EmailDraftInput
): Promise<{ id: string; version: number }> {
  // Mark any existing drafts as not current
  await getSupabaseClient()
    .from('email_drafts')
    .update({ is_current: false })
    .eq('lead_id', leadId)
    .eq('user_id', userId);

  // Get the next version number
  const { data: existing } = await getSupabaseClient()
    .from('email_drafts')
    .select('version')
    .eq('lead_id', leadId)
    .order('version', { ascending: false })
    .limit(1);

  const nextVersion = (existing?.[0]?.version ?? 0) + 1;

  // Insert the new draft
  const { data, error } = await getSupabaseClient()
    .from('email_drafts')
    .insert({
      user_id: userId,
      lead_id: leadId,
      subject: draft.subject,
      body: draft.body,
      tone: draft.tone,
      version: nextVersion,
      is_current: true,
    })
    .select('id, version')
    .single();

  if (error) throw error;
  return data;
}

export async function updateEmailDraft(
  draftId: string,
  updates: Partial<EmailDraftInput>
): Promise<void> {
  const { error } = await getSupabaseClient()
    .from('email_drafts')
    .update({
      ...updates,
      updated_at: new Date().toISOString(),
    })
    .eq('id', draftId);

  if (error) throw error;
}

export async function deleteEmailDraft(draftId: string): Promise<void> {
  const { error } = await getSupabaseClient()
    .from('email_drafts')
    .delete()
    .eq('id', draftId);

  if (error) throw error;
}

// Apollo lead sourcing
export interface SourceLeadsResult {
  success: boolean;
  leads: Lead[];
  stats: {
    totalFound: number;
    inserted: number;
    duplicatesSkipped: number;
    dncSkipped: number;
    noEmailSkipped: number;
  };
  error?: string;
}

export async function fetchApolloLeads(
  userId: string,
  count = 3
): Promise<SourceLeadsResult> {
  const supabaseUrl = (import.meta as any).env?.VITE_SUPABASE_URL;
  const supabaseAnonKey = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY;

  if (!supabaseUrl) {
    throw new Error('Supabase URL not configured');
  }

  const response = await fetch(`${supabaseUrl}/functions/v1/source-leads`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${supabaseAnonKey}`,
    },
    body: JSON.stringify({ user_id: userId, count }),
  });

  let data;
  try {
    data = await response.json();
  } catch {
    return {
      success: false,
      leads: [],
      stats: {
        totalFound: 0,
        inserted: 0,
        duplicatesSkipped: 0,
        dncSkipped: 0,
        noEmailSkipped: 0,
      },
      error: `Server error (${response.status}): ${response.statusText}`,
    };
  }

  if (!response.ok) {
    return {
      success: false,
      leads: [],
      stats: {
        totalFound: 0,
        inserted: 0,
        duplicatesSkipped: 0,
        dncSkipped: 0,
        noEmailSkipped: 0,
      },
      error: data.error || data.details || `Failed to source leads (${response.status})`,
    };
  }

  return data as SourceLeadsResult;
}

// Enrich lead with Apollo data (photo, email, LinkedIn, phone, etc.)
export interface EnrichLeadResult {
  success: boolean;
  email: string | null;
  first_name?: string | null;
  last_name?: string | null;
  photo_url?: string | null;
  location?: string | null;
  linkedin_url?: string | null;
  phone?: string | null;
  company_website?: string | null;
  company_industry?: string | null;
  already_revealed?: boolean;
  updates?: string[];
  message?: string;
  error?: string;
}

// Alias for backwards compatibility
export type RevealEmailResult = EnrichLeadResult;

export async function revealLeadEmail(
  userId: string,
  leadId: string
): Promise<RevealEmailResult> {
  const supabaseUrl = (import.meta as any).env?.VITE_SUPABASE_URL;
  const supabaseAnonKey = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY;

  if (!supabaseUrl) {
    throw new Error('Supabase URL not configured');
  }

  const response = await fetch(`${supabaseUrl}/functions/v1/reveal-email`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${supabaseAnonKey}`,
    },
    body: JSON.stringify({ user_id: userId, lead_id: leadId }),
  });

  let data;
  try {
    data = await response.json();
  } catch {
    return {
      success: false,
      email: null,
      error: `Server error (${response.status}): ${response.statusText}`,
    };
  }

  if (!response.ok) {
    return {
      success: false,
      email: null,
      error: data.error || data.details || 'Failed to reveal email',
    };
  }

  return data as RevealEmailResult;
}

// === Sprint 3: Reply Tracking & AI Features ===

// S3-1: Enrich lead with Apollo data
export interface EnrichLeadResult {
  success: boolean;
  enriched?: Record<string, any>;
  profile?: {
    employment_history: any[];
    education: any[];
    organization: {
      name: string;
      website_url: string;
      industry: string;
      estimated_num_employees: number;
      founded_year: number;
      short_description: string;
      latest_funding_stage: string;
      current_technologies: string[];
      keywords: string[];
    };
    signals: Array<{ type: string; details: string; strength: string }>;
  };
  message?: string;
  error?: string;
}

export async function enrichLead(
  userId: string,
  leadId: string
): Promise<EnrichLeadResult> {
  const supabaseUrl = (import.meta as any).env?.VITE_SUPABASE_URL;
  const supabaseAnonKey = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY;

  if (!supabaseUrl) {
    throw new Error('Supabase URL not configured');
  }

  const response = await fetch(`${supabaseUrl}/functions/v1/enrich-lead`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${supabaseAnonKey}`,
    },
    body: JSON.stringify({ user_id: userId, lead_id: leadId }),
  });

  const data = await response.json();

  if (!response.ok) {
    return {
      success: false,
      email: null,
      error: data.error || data.details || 'Failed to enrich lead',
    };
  }

  return data as EnrichLeadResult;
}

// S3-10: Track when an email is sent
export interface TrackSendResult {
  success: boolean;
  lead_id: string;
  contacted_at: string;
  error?: string;
}

export async function trackSend(
  userId: string,
  leadId: string,
  emailDraftId?: string,
  sentVia: 'extension' | 'webapp' | 'api' = 'extension'
): Promise<TrackSendResult> {
  const supabaseUrl = (import.meta as any).env?.VITE_SUPABASE_URL;
  const supabaseAnonKey = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY;

  if (!supabaseUrl) {
    throw new Error('Supabase URL not configured');
  }

  const response = await fetch(`${supabaseUrl}/functions/v1/track-send`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${supabaseAnonKey}`,
    },
    body: JSON.stringify({
      user_id: userId,
      lead_id: leadId,
      email_draft_id: emailDraftId,
      sent_via: sentVia,
    }),
  });

  const data = await response.json();

  if (!response.ok) {
    return {
      success: false,
      lead_id: leadId,
      contacted_at: '',
      error: data.error || data.details || 'Failed to track send',
    };
  }

  return data as TrackSendResult;
}

// S3-13: Classify a reply intent
export interface ClassifyReplyResult {
  success: boolean;
  classification: {
    intent: 'warm' | 'question' | 'not_interested' | 'ooo' | 'unsubscribe' | 'unknown';
    confidence: number;
    suggestedAction: string;
    shouldFollowUp: boolean;
  };
  lead_updated: boolean;
  error?: string;
}

export async function classifyReply(
  userId: string,
  leadId: string,
  replyBody: string,
  originalSubject?: string,
  originalBody?: string
): Promise<ClassifyReplyResult> {
  const supabaseUrl = (import.meta as any).env?.VITE_SUPABASE_URL;
  const supabaseAnonKey = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY;

  if (!supabaseUrl) {
    throw new Error('Supabase URL not configured');
  }

  const response = await fetch(`${supabaseUrl}/functions/v1/classify-reply`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${supabaseAnonKey}`,
    },
    body: JSON.stringify({
      user_id: userId,
      lead_id: leadId,
      reply_body: replyBody,
      original_subject: originalSubject,
      original_body: originalBody,
    }),
  });

  const data = await response.json();

  if (!response.ok) {
    return {
      success: false,
      classification: {
        intent: 'unknown',
        confidence: 0,
        suggestedAction: 'Review manually',
        shouldFollowUp: true,
      },
      lead_updated: false,
      error: data.error || data.details || 'Failed to classify reply',
    };
  }

  return data as ClassifyReplyResult;
}

// S3-15: Draft a follow-up response
export interface DraftFollowupResult {
  success: boolean;
  followUp: {
    subject: string;
    body: string;
    tone: string;
    suggestedNextSteps: string[];
  };
  error?: string;
}

export async function draftFollowup(
  userId: string,
  leadId: string,
  replyBody: string,
  originalEmail?: { subject?: string; body?: string }
): Promise<DraftFollowupResult> {
  const supabaseUrl = (import.meta as any).env?.VITE_SUPABASE_URL;
  const supabaseAnonKey = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY;

  if (!supabaseUrl) {
    throw new Error('Supabase URL not configured');
  }

  const response = await fetch(`${supabaseUrl}/functions/v1/draft-followup`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${supabaseAnonKey}`,
    },
    body: JSON.stringify({
      user_id: userId,
      lead_id: leadId,
      reply_body: replyBody,
      original_email: originalEmail,
    }),
  });

  const data = await response.json();

  if (!response.ok) {
    return {
      success: false,
      followUp: {
        subject: '',
        body: '',
        tone: 'friendly',
        suggestedNextSteps: [],
      },
      error: data.error || data.details || 'Failed to draft follow-up',
    };
  }

  return data as DraftFollowupResult;
}

// S3-14: Get notifications for user
export interface Notification {
  id: string;
  type: 'warm_reply' | 'new_lead' | 'daily_summary' | 'system';
  title: string;
  message: string;
  lead_id: string | null;
  is_read: boolean;
  created_at: string;
}

export async function getNotifications(
  userId: string,
  options?: { unreadOnly?: boolean; limit?: number }
): Promise<Notification[]> {
  let query = getSupabaseClient()
    .from('notifications')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (options?.unreadOnly) {
    query = query.eq('is_read', false);
  }

  if (options?.limit) {
    query = query.limit(options.limit);
  }

  const { data, error } = await query;

  if (error) throw error;
  return (data ?? []) as Notification[];
}

export async function markNotificationRead(notificationId: string): Promise<void> {
  const { error } = await getSupabaseClient()
    .from('notifications')
    .update({ is_read: true })
    .eq('id', notificationId);

  if (error) throw error;
}

export async function markAllNotificationsRead(userId: string): Promise<void> {
  const { error } = await getSupabaseClient()
    .from('notifications')
    .update({ is_read: true })
    .eq('user_id', userId)
    .eq('is_read', false);

  if (error) throw error;
}

// === Sprint 3.5: AI Email Generation with Common Ground ===

export interface CommonGround {
  type: 'city' | 'college' | 'industry' | 'workplace' | 'role';
  match: string;
  details: string;
}

export interface GeneratedEmail {
  subject: string;
  body: string;
  commonGround: CommonGround[];
  sameCity: boolean;
  suggestCoffee: boolean;
  tone: string;
}

export interface GenerateEmailResult {
  success: boolean;
  email: GeneratedEmail;
  draftId?: string;
  commonGround: CommonGround[];
  sameCity: boolean;
  suggestCoffee: boolean;
  error?: string;
}

export type EmailToneOption = 'friendly' | 'professional' | 'direct' | 'casual' | 'formal';

// === Style Learning (S3-4) ===

export interface StylePattern {
  type: 'greeting' | 'closing' | 'length' | 'formality' | 'punctuation' | 'structure';
  value: string;
  confidence: number;
}

export interface LearnedStyle {
  patterns: StylePattern[];
  preferredGreeting: string | null;
  preferredClosing: string | null;
  preferredLength: 'very_short' | 'short' | 'medium' | 'long';
  formalityLevel: 'very_casual' | 'casual' | 'neutral' | 'formal' | 'very_formal';
  usesEmojis: boolean;
  usesQuestions: boolean;
  samplePhrases: string[];
}

export interface LearnStyleResult {
  success: boolean;
  patternsLearned: number;
  preferences?: LearnedStyle;
  error?: string;
}

/**
 * Learn user's writing style from their email edits
 * Called when user saves an edited draft to capture their preferences
 */
export async function learnStyleFromEdit(
  userId: string,
  originalDraft: { subject: string; body: string },
  editedDraft: { subject: string; body: string },
  leadId?: string
): Promise<LearnStyleResult> {
  // Don't learn from identical or trivially different edits
  if (originalDraft.subject === editedDraft.subject && originalDraft.body === editedDraft.body) {
    return { success: true, patternsLearned: 0 };
  }

  const supabaseUrl = (import.meta as any).env?.VITE_SUPABASE_URL;
  const supabaseAnonKey = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY;

  if (!supabaseUrl) {
    return { success: false, patternsLearned: 0, error: 'Supabase URL not configured' };
  }

  try {
    const response = await fetch(`${supabaseUrl}/functions/v1/learn-style`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${supabaseAnonKey}`,
      },
      body: JSON.stringify({
        user_id: userId,
        original_draft: originalDraft,
        edited_draft: editedDraft,
        lead_id: leadId,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        patternsLearned: 0,
        error: data.error || 'Failed to learn style',
      };
    }

    return data as LearnStyleResult;
  } catch (error: any) {
    console.error('Learn style error:', error);
    return {
      success: false,
      patternsLearned: 0,
      error: error.message || 'Failed to learn style',
    };
  }
}

export async function generateEmail(
  userId: string,
  leadId: string,
  tone: EmailToneOption = 'friendly'
): Promise<GenerateEmailResult> {
  const supabaseUrl = (import.meta as any).env?.VITE_SUPABASE_URL;
  const supabaseAnonKey = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY;

  if (!supabaseUrl) {
    throw new Error('Supabase URL not configured');
  }

  const response = await fetch(`${supabaseUrl}/functions/v1/generate-email`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${supabaseAnonKey}`,
    },
    body: JSON.stringify({
      user_id: userId,
      lead_id: leadId,
      tone,
    }),
  });

  const data = await response.json();

  if (!response.ok) {
    return {
      success: false,
      email: {
        subject: '',
        body: '',
        commonGround: [],
        sameCity: false,
        suggestCoffee: false,
        tone,
      },
      commonGround: [],
      sameCity: false,
      suggestCoffee: false,
      error: data.error || data.details || 'Failed to generate email',
    };
  }

  return data as GenerateEmailResult;
}
